import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

const features = [
  'Unlimited grammar exercises',
  'Unlimited reading stories',
  'Unlimited listening exercises',
  'AI-powered speaking practice',
  'Pronunciation feedback',
  'Adaptive difficulty levels',
  'Progress tracking',
  'Ad-free experience',
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/stripe/create-checkout`, {
        plan: 'kids',
        billingCycle: 'monthly',
        userId: currentUser.uid,
        userEmail: currentUser.email,
        successUrl: `${window.location.origin}/app?subscription=success`,
        cancelUrl: `${window.location.origin}/pricing?subscription=cancelled`,
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-4xl">{'\u{1F433}'}</span>
            <span className="text-2xl font-bold text-kids-primary dark:text-indigo-400">Litrlly Kids</span>
          </Link>
          <div className="flex items-center gap-4">
            {currentUser ? (
              <Link to="/app">
                <Button variant="primary" size="small">Go to App</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="primary" size="small">Log In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="text-6xl mb-4">{'\u{1F4B0}'}</div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Simple Pricing for Kids
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              One plan with everything included. No hidden fees.
            </p>
          </div>

          {/* Pricing Card */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl dark:shadow-slate-900 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-8 text-white text-center">
              <div className="text-lg font-medium mb-2">Kids Premium</div>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-bold">$9.99</span>
                <span className="text-indigo-200">CAD/month</span>
              </div>
              <div className="text-indigo-200 text-sm mt-2">
                Cancel anytime
              </div>
            </div>

            {/* Features */}
            <div className="p-8">
              <h3 className="font-bold text-gray-800 dark:text-white mb-4">Everything included:</h3>
              <ul className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="text-kids-success text-xl">{'\u2713'}</span>
                    <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Error Message */}
              {error && (
                <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-4 text-red-700 dark:text-red-400 text-center mb-4">
                  {error}
                </div>
              )}

              {/* CTA Button */}
              <Button
                onClick={handleSubscribe}
                variant="primary"
                size="large"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : currentUser ? 'Subscribe Now' : 'Sign Up to Subscribe'}
              </Button>

              {/* Security Note */}
              <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
                <span>{'\u{1F512}'}</span>
                Secure payment powered by Stripe
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
              Common Questions
            </h2>
            <div className="space-y-4">
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2">What ages is this for?</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Litrlly Kids is designed for children ages 4-12. The difficulty automatically adjusts based on your child's age and progress.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6">
                <h3 className="font-bold text-gray-800 dark:text-white mb-2">Is there a free trial?</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  You can try the app for free with limited exercises before subscribing. Sign up to get started!
                </p>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-kids-primary dark:text-indigo-400 font-bold hover:underline">
              {'\u2190'} Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
